import { IFloorPayload } from "../../../../../Domain/Types/FloorPlan/FloorPayload.type";
import type { ServiceOrderDto } from "../Domain/BookingServicePrompt.types";
import { isServiceItemOrderable } from "./filterByServiceTime.function";

/** True when the conference service order has any non-empty selection for this inventory. */
export function hasConferenceServiceOrderSelection(order: ServiceOrderDto | undefined): boolean {
  if (!order) return false;
  if (order.caterings?.some(c => c.qty > 0)) return true;
  if ((order.bookableEquipments?.length ?? 0) > 0) return true;
  if ((order.roomLayout?.length ?? 0) > 0) return true;
  return false;
}

export function hasMandatory<T extends { selectingIsMandatory?: boolean }>(arr: T[]): boolean {
  return arr.some(item => item.selectingIsMandatory === true);
}

export function hasServicePlanMandatory({
  floorPlanForService,
  selectedZone
}: {
  floorPlanForService: IFloorPayload | undefined;
  selectedZone: number | undefined;
}) {
  if (!floorPlanForService || !selectedZone) return false;

  const isCateringMandatory = hasMandatory(
    floorPlanForService?.cateringMenuAssignments?.find(cat => cat.zoneScheduleId === selectedZone)
      ?.cateringMenuIds ?? []
  );
  const isBookEquipMandatory = hasMandatory(
    floorPlanForService?.bookableEquipmentAssignments?.find(
      eq => eq.zoneScheduleId === selectedZone
    )?.bookableEquipmentIds ?? []
  );
  const isRoomLayoutMandatory = hasMandatory(
    floorPlanForService?.roomLayoutAssignments?.find(
      layout => layout.zoneScheduleId === selectedZone
    )?.roomLayoutIds ?? []
  );

  return isCateringMandatory || isBookEquipMandatory || isRoomLayoutMandatory;
}

export function hasServicePlanExists({
  floorPlanForService,
  selectedZone
}: {
  floorPlanForService: IFloorPayload | undefined;
  selectedZone: number | undefined;
}) {
  // If payload missing or selectedZone is null/undefined, stop.
  if (!floorPlanForService || selectedZone == null) return false;

  const catering = floorPlanForService.cateringMenuAssignments?.find(
    c => c.zoneScheduleId === selectedZone
  );

  const equipment = floorPlanForService.bookableEquipmentAssignments?.find(
    e => e.zoneScheduleId === selectedZone
  );

  const roomLayout = floorPlanForService.roomLayoutAssignments?.find(
    r => r.zoneScheduleId === selectedZone
  );

  // Return true if ANY category has at least one ID
  return (
    (catering?.cateringMenuIds.length ?? 0) > 0 ||
    (equipment?.bookableEquipmentIds.length ?? 0) > 0 ||
    (roomLayout?.roomLayoutIds.length ?? 0) > 0
  );
}

export function isOrderableServiceExist({
  floorPlanForService,
  selectedZone,
  bookingFrom,
  bookingStart,
  maxSetupTime,
  isServiceAdminBypass = false
}: {
  floorPlanForService: IFloorPayload | undefined;
  selectedZone: number | undefined;
  bookingFrom: string | null;
  bookingStart: string | null;
  maxSetupTime: number | undefined;
  isServiceAdminBypass?: boolean;
}) {
  // If payload missing or selectedZone is null/undefined, stop.
  if (!floorPlanForService || selectedZone == null || !maxSetupTime) return false;

  const catering = floorPlanForService.cateringMenuAssignments?.find(
    c => c.zoneScheduleId === selectedZone
  );
  const isOrderableCatering = catering?.cateringMenuIds.filter(cat => {
    return isServiceItemOrderable({
      bookingFrom,
      bookingStart,
      setUpTime: cat.setUpTime ?? 15,
      bookingLeadTime: cat.bookingLeadTime ?? 1,
      maxSetupTime,
      isServiceAdminBypass
    });
  });

  const equipment = floorPlanForService.bookableEquipmentAssignments?.find(
    e => e.zoneScheduleId === selectedZone
  );
  const isOrderableEquipment = equipment?.bookableEquipmentIds.filter(eq => {
    return isServiceItemOrderable({
      bookingFrom,
      bookingStart,
      setUpTime: eq.setUpTime ?? 15,
      bookingLeadTime: eq.bookingLeadTime ?? 1,
      maxSetupTime,
      isServiceAdminBypass
    });
  });

  const roomLayout = floorPlanForService.roomLayoutAssignments?.find(
    r => r.zoneScheduleId === selectedZone
  );
  const isOrderableLayout = roomLayout?.roomLayoutIds.filter(layout => {
    return isServiceItemOrderable({
      bookingFrom,
      bookingStart,
      setUpTime: layout.setUpTime ?? 15,
      bookingLeadTime: layout.bookingLeadTime ?? 1,
      maxSetupTime,
      isServiceAdminBypass
    });
  });

  // Return true if ANY category has at least one ID
  return (
    (isOrderableCatering?.length ?? 0) > 0 ||
    (isOrderableEquipment?.length ?? 0) > 0 ||
    (isOrderableLayout?.length ?? 0) > 0
  );
}

/**
 * For non-admin users: true only when every category that contains mandatory items has at least one
 * orderable item (booking lead time + setup vs max setup). If a category has no mandatory items,
 * it is not required to have orderable items here.
 *
 * Used so we do not treat the plan as "service available" when optional categories are orderable
 * but mandatory categories only contain items outside lead time.
 */
export function hasOrderableServiceInMandatoryCategories({
  floorPlanForService,
  selectedZone,
  bookingFrom,
  bookingStart,
  maxSetupTime
}: {
  floorPlanForService: IFloorPayload | undefined;
  selectedZone: number | undefined;
  bookingFrom: string | null;
  bookingStart: string | null;
  maxSetupTime: number | undefined;
}): boolean {
  if (!floorPlanForService || selectedZone == null || !maxSetupTime) return false;

  const catering = floorPlanForService.cateringMenuAssignments?.find(
    c => c.zoneScheduleId === selectedZone
  );
  const cateringIds = catering?.cateringMenuIds ?? [];
  if (hasMandatory(cateringIds)) {
    const hasOrderable = cateringIds.some(cat =>
      isServiceItemOrderable({
        bookingFrom,
        bookingStart,
        setUpTime: cat.setUpTime ?? 15,
        bookingLeadTime: cat.bookingLeadTime ?? 1,
        maxSetupTime,
        isServiceAdminBypass: false
      })
    );

    if (!hasOrderable) return false;
  }

  const equipment = floorPlanForService.bookableEquipmentAssignments?.find(
    e => e.zoneScheduleId === selectedZone
  );
  const equipmentIds = equipment?.bookableEquipmentIds ?? [];
  if (hasMandatory(equipmentIds)) {
    const hasOrderable = equipmentIds.some(eq =>
      isServiceItemOrderable({
        bookingFrom,
        bookingStart,
        setUpTime: eq.setUpTime ?? 15,
        bookingLeadTime: eq.bookingLeadTime ?? 1,
        maxSetupTime,
        isServiceAdminBypass: false
      })
    );

    if (!hasOrderable) return false;
  }

  const roomLayout = floorPlanForService.roomLayoutAssignments?.find(
    r => r.zoneScheduleId === selectedZone
  );
  const layoutIds = roomLayout?.roomLayoutIds ?? [];
  if (hasMandatory(layoutIds)) {
    const hasOrderable = layoutIds.some(layout =>
      isServiceItemOrderable({
        bookingFrom,
        bookingStart,
        setUpTime: layout.setUpTime ?? 15,
        bookingLeadTime: layout.bookingLeadTime ?? 1,
        maxSetupTime,
        isServiceAdminBypass: false
      })
    );
    if (!hasOrderable) return false;
  }

  return true;
}

/**
 * True when every service category that has mandatory items in the floor plan has a matching
 * selection in the ordered service (same rules as isMandatoryCategoryComplete per tab).
 */
export function areMandatoryConferenceServicesFulfilled({
  floorPlanForService,
  selectedZone,
  orderedService
}: {
  floorPlanForService: IFloorPayload | undefined;
  selectedZone: number | undefined;
  orderedService: ServiceOrderDto | undefined;
}): boolean {
  if (!floorPlanForService || selectedZone == null) {
    return true;
  }

  const cateringIds =
    floorPlanForService.cateringMenuAssignments?.find(c => c.zoneScheduleId === selectedZone)
      ?.cateringMenuIds ?? [];
  const equipmentIds =
    floorPlanForService.bookableEquipmentAssignments?.find(e => e.zoneScheduleId === selectedZone)
      ?.bookableEquipmentIds ?? [];
  const layoutIds =
    floorPlanForService.roomLayoutAssignments?.find(r => r.zoneScheduleId === selectedZone)
      ?.roomLayoutIds ?? [];

  const anyMandatory =
    hasMandatory(cateringIds) || hasMandatory(equipmentIds) || hasMandatory(layoutIds);

  if (!anyMandatory) {
    return true;
  }

  if (!orderedService) {
    return false;
  }

  if (hasMandatory(cateringIds) && !orderedService.caterings?.some(c => c.qty > 0)) {
    return false;
  }
  if (hasMandatory(equipmentIds) && (orderedService.bookableEquipments?.length ?? 0) === 0) {
    return false;
  }
  if (hasMandatory(layoutIds) && (orderedService.roomLayout?.length ?? 0) === 0) {
    return false;
  }

  return true;
}

/**
 * Conference zone: mandatory services exist for the zone but the user has not completed
 * required selections in Redux (missing entry or empty where a category is mandatory).
 */
export function isMandatoryConferenceServiceIncomplete({
  isConferenceZoneBooking,
  floorPlanForService,
  selectedZone,
  conferenceServiceInventoryKey,
  conferenceService
}: {
  isConferenceZoneBooking: boolean;
  floorPlanForService: IFloorPayload | undefined;
  selectedZone: number | undefined;
  conferenceServiceInventoryKey: number | undefined;
  conferenceService: { [inventoryId: number]: ServiceOrderDto | undefined } | undefined;
}): boolean {
  if (!isConferenceZoneBooking) {
    return false;
  }
  if (!hasServicePlanMandatory({ floorPlanForService, selectedZone })) {
    return false;
  }

  const orderedService =
    conferenceServiceInventoryKey == null
      ? undefined
      : conferenceService?.[conferenceServiceInventoryKey];

  return !areMandatoryConferenceServicesFulfilled({
    floorPlanForService,
    selectedZone,
    orderedService
  });
}
